import * as XLSX from 'xlsx';

export type WhatsAppContactImportRow = Record<string, string>;

function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function rowsFromAoA(rows: unknown[][]): WhatsAppContactImportRow[] {
  if (!rows.length) return [];

  const headerRow = rows[0].map(normalizeHeader);
  const data: WhatsAppContactImportRow[] = [];

  for (let i = 1; i < rows.length; i += 1) {
    const line = rows[i];
    if (!Array.isArray(line)) continue;

    const row: WhatsAppContactImportRow = {};
    let hasValue = false;

    headerRow.forEach((header, idx) => {
      if (!header) return;
      const cell = String(line[idx] ?? '').trim();
      if (cell) hasValue = true;
      row[header] = cell;
    });

    if (hasValue) data.push(row);
  }

  return data;
}

function parseCsvText(text: string): WhatsAppContactImportRow[] {
  const workbook = XLSX.read(text, { type: 'string' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
  return rowsFromAoA(rows as unknown[][]);
}

function parseExcelBuffer(buffer: ArrayBuffer): WhatsAppContactImportRow[] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const allRows: WhatsAppContactImportRow[] = [];

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
    allRows.push(...rowsFromAoA(rows as unknown[][]));
  }

  return allRows;
}

/** Parse uploaded CSV or Excel file into import rows for the contacts API. */
export async function parseWhatsAppContactFile(file: File): Promise<WhatsAppContactImportRow[]> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (ext === 'csv' || ext === 'txt') {
    const text = await file.text();
    const rows = parseCsvText(text);
    if (!rows.length) throw new Error('No contact rows found. Use the template headers in the first row.');
    return rows;
  }

  if (ext === 'xlsx' || ext === 'xls') {
    const buffer = await file.arrayBuffer();
    const rows = parseExcelBuffer(buffer);
    if (!rows.length) throw new Error('No contact rows found. Use the template headers in the first row.');
    return rows;
  }

  throw new Error('Unsupported file type. Upload .csv, .xlsx, or .xls');
}

/** Parse pasted CSV text (optional fallback). */
export function parseWhatsAppContactCsvText(text: string): WhatsAppContactImportRow[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  return parseCsvText(trimmed);
}
